import { useEffect, useState } from "react";
import Chessboard from "../Chessboard";
import { Chess } from "chess.js";
import { Position } from "../models";
import useSound from "use-sound";
import moveSelfSound from "./../assets/sound-effects/move-self.mp3";
import PositionsSidePanel from "./PositionsSidePanel";
import { getLichessOpeningData } from "../getOpeningBookMove";
import { fetchPositions } from "./fetchPositions";
import _ from "lodash";
import { useStorage } from "../ZustandState";
import useKeypress from "react-use-keypress";
import { useDisclosure } from "@mantine/hooks";
import Header from "../Header";
import { useQuery } from "react-query";
import { getUser, redirectToPremiumIfNotPremium } from "../Authentication";
import { INITIAL_POSITION } from "../utils";

const Positions = () => {
    const [game, setGame] = useState<Chess>();
    const [selectedPosition, setSelectedPosition] = useState<Position>();
    const [playMoveSelfSound] = useSound(moveSelfSound);
    const { data: positions } = useQuery(["positions"], () => fetchPositions());

    const playerColor = useStorage((state) => state.playerColor);
    const setPlayerColor = useStorage((state) => state.setPlayerColor);
    const [redoStack, setRedoStack] = useState<string[]>([]);
    const [openedAddPositionModal, { open: openAddPositionModal, close: closeAddPositionModal }] = useDisclosure(false);

    const { data: user, isLoading: isLoadingUser } = useQuery(["user"], () => getUser());
    const openedEditModal = useStorage((state) => state.openedEditModal);

    redirectToPremiumIfNotPremium(user);

    useEffect(() => {
        if (!positions) {
            return;
        }

        setSelectedPosition(INITIAL_POSITION);

        const game = new Chess(INITIAL_POSITION.fen);
        setGame(game);
        if (game.turn() === "b") {
            setPlayerColor("b");
        }
    }, [positions]);

    useKeypress(["ArrowLeft", "ArrowRight"], (event: any) => {
        if (openedAddPositionModal || openedEditModal) {
            return;
        }
        if (event.key === "ArrowLeft") {
            const lastMove = _.last(game?.history());
            if (lastMove) {
                setRedoStack([...redoStack, lastMove]);
                game?.undo();
                handleOnMove(true);
            }
        } else {
            const lastMove = _.last(redoStack);
            if (lastMove) {
                game?.move(lastMove);
                setRedoStack(redoStack.slice(0, redoStack.length - 1));
                handleOnMove(true);
            }
        }
    });

    if (!selectedPosition) {
        return;
    }

    const handleOnMove = async (didUndoOrRedo?: boolean) => {
        if (!game) {
            return;
        }

        playMoveSelfSound();

        if (!didUndoOrRedo) {
            setRedoStack([]);
        }

        const openingResponse = await getLichessOpeningData(game);

        const lastMove = _.last(game.history());
        const moveNumber = game.history().length;
        const moveText = lastMove
            ? `${Math.ceil(moveNumber / 2)}${moveNumber % 2 === 0 ? "..." : "."} ${lastMove}`
            : "";
        const openingName =
            openingResponse.opening?.name ?? `${selectedPosition.originalName || selectedPosition.name} ${moveText}`;

        setSelectedPosition({
            name: openingName,
            fen: game.fen(),
            eco: openingResponse.opening?.eco,
            originalName: openingResponse.opening?.name ?? (selectedPosition.originalName || selectedPosition.name),
        });
        return;
    };

    const handleSetSelectedPosition = (position: Position) => {
        if (!position.fen) {
            throw new Error("Fen for position not found");
        }

        if (position.pgn) {
            game?.loadPgn(position.pgn);
        } else {
            game?.load(position.fen);
        }
        setSelectedPosition(position);
    };

    if (!game) {
        return;
    }

    return (
        <div>
            <Header user={user} selected="positions" />
            <div className="flex justify-center max-[1023px]:flex-col [@media(max-width:1023px)]:items-center max-[md]:justify-center max-[md]:space-y-6 [@media(max-height:870px)]:mt-16">
                <Chessboard
                    game={game}
                    onMove={handleOnMove}
                    playerColor={playerColor}
                    result={undefined}
                    state="positions"
                />
                <PositionsSidePanel
                    game={game}
                    selectedPosition={selectedPosition}
                    setSelectedPosition={handleSetSelectedPosition}
                    playerColor={playerColor}
                    setPlayerColor={setPlayerColor}
                    positions={positions}
                    openedAddPositionModal={openedAddPositionModal}
                    closeAddPositionModal={closeAddPositionModal}
                    openAddPositionModal={openAddPositionModal}
                />
            </div>
        </div>
    );
};

export default Positions;
